import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApolloError } from '@apollo/client/core';
import { QueryRef } from 'apollo-angular';
import { ConfirmationService } from 'primeng/api';
import { take } from 'rxjs/operators';
import { SubSink } from 'subsink';

import {
  GetConfigValuesGQL,
  GetConfigValuesQuery,
  GetConfigValuesQueryVariables,
  RemoveRingCentralWebhookGQL,
  RingCentralWebhookStatusFieldsFragment,
  RingCentralWebhookStatusGQL,
  SetConfigValuesGQL,
  SetUpRingCentralWebhookGQL,
} from '../../generated/graphql.generated';
import { BrandingService } from '../services/branding.service';
import { FreyaNotificationsService } from '../services/freya-notifications.service';
import { getApolloErrorMessage } from '../utilities/errors.util';

@Component({
  selector: 'app-ringcentral',
  templateUrl: './ringcentral.component.html',
  styleUrls: ['./ringcentral.component.scss']
})
export class RingcentralComponent implements OnInit, OnDestroy {

  ringCentralForm = new UntypedFormGroup({
    phoneNumber: new UntypedFormControl('', [Validators.required]),
  });

  subs = new SubSink();

  isEnabled = false;
  isConnected = false;

  webhookStatus?: RingCentralWebhookStatusFieldsFragment;
  isWebhookLoading = false;
  isWebhookSaving = false;

  configValuesQueryRef: QueryRef<GetConfigValuesQuery, GetConfigValuesQueryVariables>;

  constructor(
    private notify: FreyaNotificationsService,
    private getConfigValuesGQL: GetConfigValuesGQL,
    private setConfigValuesGQL: SetConfigValuesGQL,
    private ringCentralWebhookStatusGQL: RingCentralWebhookStatusGQL,
    private setUpRingCentralWebhookGQL: SetUpRingCentralWebhookGQL,
    private removeRingCentralWebhookGQL: RemoveRingCentralWebhookGQL,
    private route: ActivatedRoute,
    private router: Router,
    private branding: BrandingService,
    private confirmationService: ConfirmationService,
  ) { }

  ngOnInit(): void {
    // Check for query params (status from callback redirect)
    this.route.queryParams.subscribe(params => {
        if (params.status === 'success') {
            this.notify.success('RingCentral connected successfully!');
            // Remove query params
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { status: null, message: null },
                queryParamsHandling: 'merge'
            });
        } else if (params.status === 'error') {
            this.notify.warning(`RingCentral connection failed: ${params.message}`);
        }
    });

    this.configValuesQueryRef = this.getConfigValuesGQL.watch({
      keys: [
        'ringcentral.enabled',
        'ringcentral.tokenData', // To check if connected
        'ringcentral.phoneNumber',
      ]
    });

    this.subs.sink = this.configValuesQueryRef.valueChanges
      .subscribe((res) => {
        if (res.loading) { return; }

        if (!res.data.getConfigValues.length) { return; }

        const configs = res.data.getConfigValues;

        const enabledConfig = configs.find(c => c.key === 'ringcentral.enabled');
        const tokenConfig = configs.find(c => c.key === 'ringcentral.tokenData');
        const phoneConfig = configs.find(c => c.key === 'ringcentral.phoneNumber');

        try {
          this.isEnabled = enabledConfig ? JSON.parse(enabledConfig.value) : false;
        } catch (_e) {
          this.isEnabled = false;
        }

        this.isConnected = !!tokenConfig && !!tokenConfig.value;

        this.ringCentralForm.patchValue({
          phoneNumber: phoneConfig?.value || '',
        });

        this.setFormDisabled(!this.isEnabled);
      });

    this.loadWebhookStatus();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  saveSettings() {
    const { phoneNumber } = this.ringCentralForm.value;

    this.setConfigValuesGQL.mutate({
      configs: [
        {
          key: 'ringcentral.phoneNumber',
          value: phoneNumber,
        }
      ]
    }).subscribe(() => {
      this.configValuesQueryRef.refetch();
      this.notify.success('RingCentral settings saved');
    }, (err) => {
      this.notify.apolloError('Failed to save settings', err);
    });
  }

  toggleEnabled() {
    const enabled = this.isEnabled;

    this.setConfigValuesGQL.mutate({
      configs: [{
        key: 'ringcentral.enabled',
        value: JSON.stringify(enabled),
      }]
    }).subscribe({
      next: () => {
        this.configValuesQueryRef.refetch();
        this.notify.success(`RingCentral ${enabled ? 'enabled' : 'disabled'}`);
      },
      error: (err) => {
        this.isEnabled = !enabled;
        this.notify.apolloError('Failed to update RingCentral integration', err);
      },
    });
  }

  loadWebhookStatus() {
    this.isWebhookLoading = true;

    this.ringCentralWebhookStatusGQL.fetch({}, { fetchPolicy: 'network-only' }).subscribe({
      next: (res) => {
        this.isWebhookLoading = false;
        this.webhookStatus = res.data.ringCentralWebhookStatus;
      },
      error: (err) => {
        this.isWebhookLoading = false;
        this.webhookStatus = { state: 'error', error: err.message };
      },
    });
  }

  setUpWebhook() {
    this.isWebhookSaving = true;

    this.setUpRingCentralWebhookGQL.mutate().subscribe({
      next: (res) => {
        this.isWebhookSaving = false;
        this.webhookStatus = res.data.setUpRingCentralWebhook;
        this.notify.success('RingCentral webhook set up');
      },
      error: (err) => {
        this.isWebhookSaving = false;
        this.notify.apolloError('Failed to set up RingCentral webhook', err);
        this.loadWebhookStatus();
      },
    });
  }

  removeWebhook() {
    this.isWebhookSaving = true;

    this.removeRingCentralWebhookGQL.mutate().subscribe({
      next: (res) => {
        this.isWebhookSaving = false;
        this.webhookStatus = res.data.removeRingCentralWebhook;
        this.notify.success('RingCentral webhook removed');
      },
      error: (err) => {
        this.isWebhookSaving = false;
        this.notify.apolloError('Failed to remove RingCentral webhook', err);
        this.loadWebhookStatus();
      },
    });
  }

  setFormDisabled(disabled: boolean) {
    if (disabled) {
      this.ringCentralForm.disable();
    } else {
      this.ringCentralForm.enable();
    }
  }

  connectRingCentral() {
    this.subs.sink = this.branding.currentZone()
        .pipe(take(1))
        .subscribe(zone => {
        const redirectUrl = new URL(window.location.href);
        redirectUrl.searchParams.set('zone', zone.id);
        redirectUrl.pathname = `/api/ringcentral/auth`;
        
        window.location.href = redirectUrl.toString();
    });
  }

  disconnectRingCentral() {
      this.confirmationService.confirm({
          message: 'Are you sure you want to disconnect RingCentral? This will stop SMS services and remove webhooks.',
          header: 'Disconnect RingCentral',
          icon: 'pi pi-exclamation-triangle',
          accept: () => {
              this.removeRingCentralWebhookGQL.mutate().subscribe({
                  next: () => this.clearConnection(),
                  error: (err) => this.confirmDisconnectWithWebhook(err),
              });
          }
      });
  }

  private confirmDisconnectWithWebhook(err: ApolloError) {
      this.confirmationService.confirm({
          message: `RingCentral couldn't remove the webhook: ${getApolloErrorMessage(err)}. `
              + 'Disconnect anyway? The webhook may keep delivering messages.',
          header: 'Webhook not removed',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Disconnect anyway',
          rejectLabel: 'Stay connected',
          accept: () => this.clearConnection(),
          reject: () => this.loadWebhookStatus(),
      });
  }

  private clearConnection() {
      this.setConfigValuesGQL.mutate({
          configs: [
              { key: 'ringcentral.tokenData', value: null },
              { key: 'ringcentral.enabled', value: 'false' },
              // We can keep phoneNumber if desired, but user might want to clear it.
              // Let's keep it for convenience if they reconnect.
          ]
      }).subscribe(() => {
          this.configValuesQueryRef.refetch();
          this.loadWebhookStatus();
          this.notify.success('RingCentral disconnected successfully');
      }, (err) => {
          this.notify.apolloError('Failed to disconnect RingCentral', err);
      });
  }
}
